import { observer } from 'mobx-react';
import { FC, Fragment, useState } from 'react';

import { Button, Combobox, Flex, Text } from '@servicetitan/anvil2';
import { useDependencies } from '@servicetitan/react-ioc';

import { InstalledEquipmentApiModel } from '../../common/api/equipments.api';
import { BottomDrawerSelect } from '../../common/components/bottom-drawer-select/bottom-drawer-select';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { SelectedEquipmentInfo } from '../../common/stores/chat.store';
import { EquipmentPickerStore } from '../store/equipment-picker.store';

interface SearchEquipmentProps {
    onSubmit: (equipment: InstalledEquipmentApiModel) => void;
    showWithDocsOnly?: boolean;
}

export const getEquipmentLabel = (equipment: SelectedEquipmentInfo) => {
    const parts = [equipment.manufacturer, equipment.name, equipment.model];
    return parts.filter(x => !!x).join(' - ');
};

export const SearchEquipment: FC<SearchEquipmentProps> = observer(
    ({ onSubmit, showWithDocsOnly }) => {
        const [equipmentPickerStore] =
            useDependencies<[EquipmentPickerStore]>(EquipmentPickerStore);

        const [selectedEquipment, setSelectedEquipment] = useState<
            InstalledEquipmentApiModel | undefined
        >(equipmentPickerStore.selectedEquipment);

        const [drawerOpen, setDrawerOpen] = useState(false);
        const [searchText, setSearchText] = useState('');

        const query = searchText.toLowerCase();
        const options = equipmentPickerStore.equipments
            .filter(x => !showWithDocsOnly || x.mapped_equipment_ids)
            .filter(e => e.name?.toLowerCase().includes(query))
            .map(e => ({
                ...e,
                label: getEquipmentLabel(e),
            }));

        const handleSubmit = () => {
            if (selectedEquipment) {
                onSubmit(selectedEquipment);
            }
        };

        return (
            <Fragment>
                <MessageBubble isMine={false}>
                    <Flex direction="column" gap="4">
                        <Text size="small">Select from equipment at this location:</Text>

                        <div onClick={() => setDrawerOpen(true)}>
                            <Combobox
                                open={false}
                                items={[selectedEquipment]}
                                selectedItem={selectedEquipment}
                                onChange={(item: InstalledEquipmentApiModel) => {
                                    setSelectedEquipment(item);
                                    setDrawerOpen(false);
                                }}
                                itemToString={item => item?.label ?? ''}
                                itemToKey={item => (item ? item.equipment_id : null)}
                            >
                                <Combobox.SelectTrigger
                                    error={
                                        selectedEquipment &&
                                        !selectedEquipment.has_document_coverage &&
                                        'No documentation available yet, responses may be inaccurate'
                                    }
                                    value={selectedEquipment?.label ?? ''}
                                    label="Location Equipment"
                                    placeholder="Search Equipment..."
                                />
                            </Combobox>
                        </div>
                        <Flex>
                            <Button
                                appearance="primary"
                                disabled={!selectedEquipment}
                                loading={equipmentPickerStore.isFetchingInfo}
                                onClick={handleSubmit}
                            >
                                Submit
                            </Button>
                        </Flex>
                    </Flex>
                </MessageBubble>

                <BottomDrawerSelect
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    dropdownOptions={options}
                    itemToKey={item => item.equipment_id}
                    searchPlaceholder="Search manufacturer"
                    onSearchQueryChange={setSearchText}
                    onChange={item => {
                        setSelectedEquipment(item);
                        setDrawerOpen(false);
                    }}
                    value={selectedEquipment}
                    getIsItemErrored={item => !item.has_document_coverage}
                />
            </Fragment>
        );
    }
);
